import React, { useEffect, useRef, useState } from 'react'
import { X, Camera, AlertCircle, Loader2 } from 'lucide-react'
import { Html5Qrcode } from 'html5-qrcode'

function QRScanner({ isOpen, onClose, onScan }) {
  const scannerRef = useRef(null)
  const [error, setError] = useState(null)
  const [isScanning, setIsScanning] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Cleanup scanner on unmount or when modal closes
  useEffect(() => {
    return () => {
      stopScanner()
    }
  }, [])

  // Auto-start camera when modal opens
  useEffect(() => {
    if (isOpen && !isScanning && !isLoading) {
      startCamera()
    } else if (!isOpen) {
      stopScanner()
    }
  }, [isOpen])

  const stopScanner = async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop()
        await scannerRef.current.clear()
      } catch (err) {
        console.log('Scanner already stopped')
      }
      setIsScanning(false)
    }
  }

  const parseUPIString = (upiString) => {
    try {
      // UPI QR code format: upi://pay?pa=<UPI_ID>&pn=<NAME>&...
      const url = new URL(upiString)
      const params = new URLSearchParams(url.search)

      return {
        upiId: params.get('pa') || params.get('upi'),
        merchantName: params.get('pn') || params.get('name'),
        amount: params.get('am') || params.get('amount'),
        note: params.get('tn') || params.get('note')
      }
    } catch (err) {
      // If it's just a UPI ID directly
      if (upiString.includes('@')) {
        return { upiId: upiString, merchantName: '' }
      }
      return null
    }
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setIsLoading(true)
      setError(null)

      // Stop any active camera scanning first
      if (scannerRef.current && isScanning) {
        await stopScanner()
        // Wait for cleanup
        await new Promise(resolve => setTimeout(resolve, 500))
      }

      // Create a fresh scanner instance for file scanning
      const fileScanner = new Html5Qrcode('qr-reader')

      // Scan the uploaded file
      const decodedText = await fileScanner.scanFile(file, true)
      console.log('📷 QR Code detected from file:', decodedText)

      // Clear the file scanner
      try {
        await fileScanner.clear()
      } catch (e) {
        console.log('Scanner clear:', e)
      }

      const parsedData = parseUPIString(decodedText)
      if (parsedData && parsedData.upiId) {
        onScan(parsedData)
        onClose()
      } else {
        setError('No valid UPI QR code found in the image')
      }

      setIsLoading(false)
    } catch (err) {
      console.error('File scan error:', err)

      // More specific error messages
      if (err.message && err.message.includes('QR code parse error')) {
        setError('Could not find a QR code in the image. Please try a clearer image.')
      } else {
        setError(`Scan failed: ${err.message || 'Please ensure the image contains a valid QR code'}`)
      }

      setIsLoading(false)
    } finally {
      // Reset file input
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  const startCamera = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Create scanner instance
      const html5QrCode = new Html5Qrcode('qr-reader')
      scannerRef.current = html5QrCode

      // Configure camera
      const config = {
        fps: 10, // Scans per second
        qrbox: { width: 250, height: 250 }, // Square scanning box
        aspectRatio: 1.0 // Square aspect ratio
      }

      // Success callback when QR code is scanned
      const onScanSuccess = (decodedText, decodedResult) => {
        console.log('📷 QR Code detected:', decodedText)

        const parsedData = parseUPIString(decodedText)
        if (parsedData && parsedData.upiId) {
          stopScanner()
          onScan(parsedData)
        } else {
          // If not a valid UPI code, try treating it as plain text
          console.log('Non-UPI QR code:', decodedText)
        }
      }

      // Error callback (called frequently, can be ignored mostly)
      const onScanError = (errorMessage) => {
        // Ignore verbose scanning errors
        // Only log actual errors, not "No QR code found"
      }

      // Start scanning with back camera preference
      await html5QrCode.start(
        { facingMode: 'environment' }, // Prefer back camera
        config,
        onScanSuccess,
        onScanError
      )

      setIsScanning(true)
      setIsLoading(false)
    } catch (err) {
      console.error('Camera access error:', err)

      if (err.toString().includes('NotAllowedError')) {
        setError('Camera permission denied. Please allow camera access.')
      } else if (err.toString().includes('NotFoundError')) {
        setError('No camera found on this device.')
      } else {
        setError('Could not access camera. Please try again.')
      }

      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <Camera className="h-5 w-5 text-primary-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Scan QR Code
            </h3>
          </div>
          <button
            onClick={() => {
              stopScanner()
              onClose()
            }}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scanner Area - Square with Aspect Ratio */}
        <div className="relative bg-black aspect-square flex items-center justify-center overflow-hidden">
          {!isScanning && !isLoading && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-20">
              <button
                onClick={startCamera}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 rounded-xl font-bold shadow-lg shadow-primary-500/30 transition-all active:scale-95"
              >
                Open Camera
              </button>
              <p className="mt-4 text-xs text-gray-400">Click to start camera</p>
            </div>
          )}

          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-20">
              <Loader2 className="h-10 w-10 animate-spin text-primary-500 mb-2" />
              <p className="text-sm">Processing...</p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-20 p-6 text-center">
              <AlertCircle className="h-10 w-10 text-red-500 mb-2" />
              <p className="text-sm font-medium mb-4">{error}</p>
              <button
                onClick={startCamera}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm"
              >
                Try Again
              </button>
            </div>
          )}

          {/* QR Code Scanner Container */}
          <div id="qr-reader" className="w-full" />
        </div>

        {/* Upload Option */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {isScanning ? '🔍 Scanning for QR codes...' : 'Or upload a QR code image'}
            </p>

            <label className="inline-flex items-center justify-center px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg cursor-pointer transition-colors text-sm font-medium">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Upload QR Image
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isLoading}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QRScanner